/**
 * This file contains utils that can be used in tests without causing unnecessary errors.
 */
import {
  AvailabilityError,
  AvailabilityErrorType,
  FieldUpdate,
  ProductLocationModel,
  ProductModel,
} from 'interfaces'
import { Vehicle } from '../../../../src/store/models/Vehicles'
import { LocationType } from '../../../../src/helpers/locationType'
import { Prices } from '../../../../src/store/cart/ValidationInterfaces'
import {
  ICartErrorProduct,
  SellerCartData,
  SellerCartVehicle,
} from '../store/ISellerCart'
import { VehicleShort } from '../../../../src/api/cart/interfaces'
import { MY_PLACE_SELLER } from 'common'
import { Address } from 'src/api/user/interfaces'
import { MISCELLANEOUS_CART_ID } from 'src/features/search/Results/utils/miscellaneousVehicle'
import { store } from 'store'

export const isSameVehicle = (
  vehicle1: Vehicle,
  vehicle2: Vehicle
): boolean => {
  return (
    vehicle1.year?.id === vehicle2.year?.id &&
    vehicle1.make?.id === vehicle2.make?.id &&
    vehicle1.model?.id === vehicle2.model?.id &&
    vehicle1.engine?.id === vehicle2.engine?.id &&
    // TODO: check if comparing ID is enough.
    vehicle1.id === vehicle2.id
  )
}

export const isSameVehicleShort = (
  vehicle1: VehicleShort,
  vehicle2: VehicleShort
): boolean => {
  if (vehicle1.id && vehicle2.id) {
    if (vehicle1.id.toString() === vehicle2.id.toString()) {
      return true
    }
    // If ids are present and are not same, it is not the same vehicle.
    return false
  }
  return (
    vehicle1.year === vehicle2.year &&
    vehicle1.make === vehicle2.make &&
    vehicle1.model === vehicle2.model &&
    vehicle1.engine === vehicle2.engine
  )
}

/**
 * Products match if the partNumber, lineCode, description, and year all match
 */
export const productsMatch = (p1: ProductModel, p2: ProductModel): boolean => {
  return (
    p1.partNumber === p2.partNumber &&
    p1.lineCode === p2.lineCode &&
    p1.description === p2.description
    // && p1.fromYear === p2.fromYear
  )
}

export const autoSelectLocation = (
  locations: ProductLocationModel[],
  qty: number
): ProductLocationModel => {
  const match = locations.find(
    (loc) =>
      loc.isSelected &&
      loc.locType !== LocationType.VIEW_ONLY &&
      loc.qtyAvailable >= qty
  )
  return match ?? locations[0]
}

export const selectLocationByQty = (
  product: ProductModel,
  qtyRequested: number,
  inCart = false
): ProductLocationModel => {
  const locations = product.location ?? []

  const match = locations.find((location) => {
    return inCart
      ? location.qtyAvailable >= qtyRequested &&
          location.locType !== LocationType.VIEW_ONLY &&
          location.isSelected
      : location.qtyAvailable >= qtyRequested && location.isSelected
  })
  if (match) {
    return match
  }
  /* Here you can see how we're setting the primary
  location as default if the user doesn't have any available locations
  ALS(II.a4)
  */
  return locations[0]
}

export const allowOrderWithoutAvailableInventory = (
  location: ProductLocationModel,
  orderIfNotAvailable: boolean
): boolean => {
  /* This function is used to switch the ALS(II.a)
  part of the flow to ALS(II.a1) or ALS(II.a2), which means,
  detecting if a location change should be overrided based on
  if the user has the user preference findit_orderIfNotAvail and
  the location that he's manually trying to select is the primary
  location of a product */
  return orderIfNotAvailable && location.locType === LocationType.PRIMARY
}

export const buildAvailabilityError = (
  errorType: AvailabilityErrorType,
  fieldUpdates?: Array<FieldUpdate>,
  location?: ProductLocationModel
): AvailabilityError => {
  const availabilityError: AvailabilityError = { errorType }
  if (fieldUpdates) availabilityError.fieldUpdates = fieldUpdates
  if (location) {
    availabilityError.location = location
  }
  return availabilityError
}

export const getVehicleProductsErrors = (
  sellerCartVehicle: SellerCartVehicle
): ICartErrorProduct[] => {
  return sellerCartVehicle.sellers.flatMap(
    (s) =>
      s.locations?.flatMap(
        (l) =>
          l.parts
            ?.filter((p) => p.errors?.length)
            .map((p) => ({
              product: p,
              seller: s,
              location: l,
            })) || []
      ) || []
  )
}

export const currentLocationHasQuantity = (
  product: ProductModel,
  qtyRequested: number,
  locationId: string
): boolean => {
  const locations = product.location ?? []

  const selectedLocation = locations.find(
    (item) => item.locationId.toString() === locationId.toString()
  )
  return selectedLocation && selectedLocation.qtyAvailable
    ? selectedLocation.qtyAvailable >= qtyRequested
    : false
}

export const getLocationById = (
  product: ProductModel,
  locationId: string
): ProductLocationModel => {
  const location = (product.location || []).find(
    (location) => location.locationId.toString() === locationId.toString()
  )
  if (location) {
    return location
  }
  throw new Error('Location not found')
}

export const validatePrices = (
  oldLocation: ProductLocationModel,
  newLocation: ProductLocationModel
): Array<AvailabilityError> => {
  const availabilityErrors: Array<AvailabilityError> = []
  const priceUpdates = collectPriceUpdates(oldLocation, newLocation)

  if (priceUpdates.length > 0)
    availabilityErrors.push({
      errorType: AvailabilityErrorType.PRICE_OVERRIDE,
      fieldUpdates: priceUpdates,
    })

  return availabilityErrors
}

export const buildUpdate = (
  field: string,
  oldValue: string,
  newValue: string
): FieldUpdate => {
  return {
    field,
    oldValue,
    newValue,
  }
}

export const hasPriceChanged = (
  oldVal: string | number | undefined | null,
  newVal: string | number | undefined | null
): boolean =>
  Number(Number(oldVal || 0).toFixed(2)) !==
  Number(Number(newVal || 0).toFixed(2))

const collectPriceUpdates = (
  location: ProductLocationModel,
  updatedLocation: ProductLocationModel
): Array<FieldUpdate> => {
  const updates: Array<FieldUpdate> = []
  const oldCost: string = location.cost?.toString() ?? '0'
  const oldCoreCost: string = location.coreCost?.toString() ?? '0'
  const oldCoreList: string = location.coreList?.toString() ?? '0'
  const oldListPrice: string = location.list?.toString() ?? '0'

  const newCost: string = updatedLocation.cost?.toString() ?? '0'
  const newCoreCost: string = updatedLocation.coreCost?.toString() ?? '0'
  const newCoreList: string = updatedLocation.coreList?.toString() ?? '0'
  const newListPrice: string = updatedLocation.list?.toString() ?? '0'

  if (hasPriceChanged(oldCost, newCost))
    updates.push(buildUpdate(Prices.COST, oldCost, newCost))

  if (hasPriceChanged(oldCoreCost, newCoreCost))
    updates.push(buildUpdate(Prices.CORE, oldCoreCost, newCoreCost))

  if (hasPriceChanged(oldCoreList, newCoreList))
    updates.push(buildUpdate(Prices.CORE_LIST, oldCoreList, newCoreList))

  if (hasPriceChanged(oldListPrice, newListPrice))
    updates.push(buildUpdate(Prices.LIST, oldListPrice, newListPrice))

  return updates
}

export const isCartVehicleEmpty = (vehicle: SellerCartVehicle): boolean => {
  return (
    !doesCartVehiclehasParts(vehicle) &&
    vehicle.specifications.length === 0 &&
    vehicle.laborResults.length === 0 &&
    (vehicle.productsCantbeFulfilled?.length ?? 0) === 0
  )
}

export const checkAreAllCartsEmpty = (cartData: SellerCartData): boolean => {
  return cartData.vehicles.find((v) => !isCartVehicleEmpty(v)) === undefined
}

export const doesCartVehiclehasParts = (
  vehicle: SellerCartVehicle
): boolean => {
  return getTotalPartCountInCartVehicle(vehicle) > 0
}

export const getTotalPartCountInCartVehicle = (
  vehicle: SellerCartVehicle
): number => {
  return vehicle.sellers.flatMap((s) => s.locations).flatMap((l) => l.parts)
    .length
}

export const findNumberOfEntries = (
  coverage: string,
  vehicles: SellerCartVehicle[] = []
): number => {
  let counterEntries = 0
  vehicles.forEach((vehicle) => {
    vehicle.sellers.forEach((seller) => {
      // Only consider myplace seller.
      if (seller.coverageKey !== MY_PLACE_SELLER.coverageKey) {
        return
      }
      seller.locations.forEach((location) => {
        location.parts.forEach((part) => {
          const matchCoverage = coverage
            ?.split(',')
            .includes(String(part?.orderNumber))
          if (matchCoverage) {
            counterEntries += part.quantityRequested
          }
        })
      })
    })
  })
  return counterEntries
}

export const getAddressesByID = (
  addresses: Address[],
  ID: string
): Address | undefined => {
  return addresses.find((address) => address.id === ID)
}

export const isThereOnlyTheMiscellaneousCart = (): boolean => {
  const cartData = store.getState().cart.cartData
  const cartVehicleCheck = cartData.vehicles.some(
    (vehicles) => vehicles.vehicle.engine?.id !== MISCELLANEOUS_CART_ID
  )
  if (cartVehicleCheck) return false
  if (cartData.vehicles.length > 1) return false
  return true
}

export const vehicleProductsLength = (
  sellerCartVehicle: SellerCartVehicle
): number => {
  let sum = 0
  sellerCartVehicle.sellers.forEach((s) => {
    s.locations.forEach((l) => {
      l.parts.forEach((p) => {
        sum += p.quantityRequested
      })
    })
  })

  return sum
}
